<template>
  <div
    class="h-fit min-w-[300px] rounded-md border shadow-sm"
    :data-cy="`dynamic-offers-attributes-card-${offer.id}`"
  >
    <div class="flex flex-col rounded-md bg-white text-sm">
      <div
        class="flex flex-col space-y-4 divide-y pb-4 sm:w-full sm:items-start sm:space-y-0 sm:gap-y-4"
        data-cy="dynamic-offers-basic-attributes"
      >
        <div
          class="flex w-full flex-wrap items-center justify-between space-y-2 p-4"
        >
          <div
            class="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-4"
          >
            <div class="flex space-x-2">
              <span class="content-center">
                <img
                  v-if="offer.funder?.logo?.file_url"
                  class="max-h-6 max-w-6"
                  :src="offer.funder.logo.file_url"
                  alt="logo"
                  loading="lazy"
                />
                <icon-base
                  v-else
                  width="30"
                  height="30"
                  view-box="0 0 40 40"
                  class="h-10"
                  :icon="IconArloc"
                />
              </span>
              <div class="ml-1 flex items-center">
                <div class="flex flex-col">
                  <span
                    class="text-sm font-semibold"
                    data-cy="dynamic-offer-product"
                  >
                    {{ offer.product }}
                  </span>
                  <span
                    class="text-xs font-normal text-label"
                    data-cy="dynamic-offer-funder"
                  >
                    {{ offer.funder?.full_name }}
                  </span>
                </div>
                <icon-base
                  v-tooltip="{
                    content: offer.product_description,
                    onShow: () => !!offer.product_description,
                  }"
                  class="mt-0.5 text-gray-300"
                  height="16"
                  width="16"
                  view-box="0 0 22 22"
                  :icon="IconInfo"
                />
              </div>
            </div>
            <div class="flex space-x-4">
              <div
                class="flex h-5 max-w-max min-w-max items-center rounded-full bg-icon-user-purple/10 p-2 text-[10px] font-medium whitespace-nowrap uppercase select-none"
              >
                <span class="font-semibold text-icon-user-purple">
                  {{ $t("COMMON.CUSTOMIZABLE") }}
                </span>
              </div>
            </div>
          </div>

          <div class="flex space-x-3">
            <lf-button
              class="w-full rounded-sm bg-red-100 p-4 text-sm leading-none font-medium text-red-500"
            >
              {{ $t("COMMON.DECLINE") }}
            </lf-button>
            <lf-button class="bg-primary/10 text-primary">
              {{ $t("COMMON.ACCEPT") }}
            </lf-button>
            <lf-button
              size="small"
              class="roundedfont-medium h-10 w-full border border-gray-200 bg-white text-sm leading-none text-label"
              @click="showAdditionalAttributes = !showAdditionalAttributes"
            >
              <icon-base
                height="6"
                width="10"
                :icon="IconDropdownArrow"
                :class="{ 'rotate-180 transform': showAdditionalAttributes }"
              />
            </lf-button>
          </div>
        </div>
        <div
          class="flex flex-wrap justify-between gap-y-4 p-4 py-4 sm:w-full sm:gap-x-10 sm:py-0 sm:pt-4"
        >
          <div
            v-for="{ name, label, values, type } in primaryFields"
            :key="name"
            :data-cy="`dynamic-offers-basic-${name}`"
            class="flex w-1/2 flex-col text-xs sm:w-fit"
          >
            <span class="font-semibold text-headline">
              {{ labelForPrimaryField(values, type, name) || "-" }}
            </span>
            <span class="max-w-48 font-normal text-label">
              {{ label }}
            </span>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="showAdditionalAttributes"
      class="flex flex-wrap bg-[#F9F9F9] py-4"
    >
      <div class="mb-2 flex-auto space-y-4 px-3 sm:w-fit">
        <div class="font-semibold">
          {{ $t("OFFER_CUSTOMISATION.CALCULATE_LOAN_OFFER") }}
        </div>
        <div
          v-for="fieldName in orderDynamicFields"
          :key="`dynamic-offers-${fieldName}`"
          :data-cy="`dynamic-offers-${fieldName}`"
        >
          <dynamic-offer-field
            :offer="offer"
            :fields="dynamicFields[fieldName]"
            :field-name="fieldName"
            @update-field="updateDynamicField"
          />
        </div>
      </div>
      <div
        class="w-1/2 flex-auto space-y-2 px-3"
        data-cy="dynamic-offers-additional-attributes"
      >
        <div class="font-semibold">
          {{ $t("OFFER_CUSTOMISATION.OFFER_ESTIMATE") }}
        </div>
        <div
          v-for="{ label, value, type, name } in expandedFields"
          :key="`${offer.id}-additional-data-${name}`"
          :data-cy="`dynamic-offers-additional-${name}`"
          class="flex justify-between space-x-1 text-xs text-label"
        >
          <span class="max-w-48">
            {{ label }}
          </span>
          <span
            class="max-w-[9rem] overflow-hidden text-ellipsis whitespace-nowrap"
          >
            {{ labelForAdditionalField(value, type, name) || "-" }}
          </span>
        </div>
      </div>
      <div v-if="note" class="w-full flex-col space-y-3 px-3 text-sm">
        <div class="flex items-center">
          <icon-offer-note class="text-primary" />
          <span
            class="font-medium text-gray-500"
            data-cy="offer-note-section-title"
          >
            {{ $t("COMMON.NOTE") }}
          </span>
          <icon-base
            class="mt-0.5 ml-2 text-gray-300"
            height="16"
            width="16"
            view-box="0 0 22 22"
            :icon="IconInfo"
          />
        </div>

        <p
          class="overflow-hidden text-start break-words text-ellipsis text-gray-500"
          data-cy="offer-note"
        >
          {{ note.label }}
        </p>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, ref, watch } from "vue";

import groupBy from "lodash/groupBy";
import max from "lodash/max";
import min from "lodash/min";
import orderBy from "lodash/orderBy";
import uniq from "lodash/uniq";

import { BpOfferSection } from "@/enums/offerTemplates";
import { FieldType } from "@/enums/offers";
import { formatMoney } from "@/helpers/formatting";

import DynamicOfferField from "@/components/deals/workflow/BorrowerPlatform/BorrowerPlatformPreview/BorrowerPlatformDynamicOfferField.vue";
import IconDropdownArrow from "@/components/icons/IconDropdownArrow.vue";
import IconInfo from "@/components/icons/IconInfo.vue";
import IconArloc from "@/components/icons/fpProductTypes/IconArloc.vue";

import type { OfferTemplateDetail, OfferView } from "@/models/offers";

const props = defineProps<{
  offers: OfferView[];
  dynamicFieldNames: string[];
}>();

const showAdditionalAttributes = ref(true);
const offer = ref(props.offers[0]);

const primaryFields = computed(() => {
  const details = props.offers
    .flatMap((offer) => {
      return offer.details.filter(
        (detail) =>
          detail.name !== "note" && detail.section === BpOfferSection.PRIMARY
      );
    })
    .reduce(
      (acc, detail: OfferTemplateDetail) => {
        acc[detail.name] = {
          name: detail.name,
          label: detail.label,
          type: detail.type,
          values: [...(acc[detail.name]?.values ?? []), detail.value],
          weight: detail.weight,
          options: detail.options || {},
        };
        return acc;
      },
      {} as Record<
        string,
        {
          name: string;
          label: string;
          type: FieldType;
          values: Array<string | number>;
          weight: number;
          options: Record<string, string>;
        }
      >
    );

  return orderBy(details, "weight");
});

const dynamicFields = computed<
  Record<string, (OfferTemplateDetail & { offer_id: string })[]>
>(() => {
  const details = props.offers.flatMap((offer) =>
    offer.details
      .filter((detail) => props.dynamicFieldNames.includes(detail.name))
      .flatMap((detail) => ({ ...detail, offer_id: offer.id }))
  );

  return groupBy(orderBy(details, "value"), "name");
});

const orderDynamicFields = computed(() => {
  const details = offer.value.details.filter((detail) =>
    props.dynamicFieldNames.includes(detail.name)
  );

  return orderBy(details, "weight").map(({ name }) => name);
});

const expandedFields = computed(() => {
  const details = offer.value.details.filter(
    (detail) =>
      detail.name !== "note" && !props.dynamicFieldNames.includes(detail.name)
  );

  return orderBy(details, "weight");
});

const note = computed(() =>
  offer.value.details.find((detail) => detail.name === "note")
);

const updateDynamicField = (offerId: string) => {
  const choosenOffer = props.offers.find(({ id }) => id === offerId);
  if (choosenOffer) {
    offer.value = choosenOffer;
  }
};

const labelForPrimaryField = (
  values: (string | number)[],
  type: FieldType,
  fieldName: string
) => {
  const field = primaryFields.value.find(({ name }) => name === fieldName);
  if (type === FieldType.OPTIONS) {
    return uniq(values)
      .flatMap((value) => field?.options?.[value] || "-")
      .join(", ");
  }

  if (type === FieldType.CURRENCY) {
    return `${formatMoney(min(values))}-${formatMoney(max(values))}`;
  }

  return `${min(values)}-${max(values)}`;
};

const labelForAdditionalField = (
  value: string | number,
  type: FieldType,
  fieldName: string
) => {
  if (type === FieldType.CURRENCY) {
    return formatMoney(value);
  }

  if (type === FieldType.OPTIONS) {
    const field = expandedFields.value.find(({ name }) => name === fieldName);
    return field?.options?.[value] || "-";
  }

  return value;
};

watch(
  () => props.offers,
  () => {
    offer.value = props.offers[0];
  }
);
</script>
